/*
XOL-67 (RTA redesign v2.1, chunk V1): the RTA (Request to Acquire) stage model, in pure functions. No Vue,
no store, no axios: components wrap these in `computed` (CLAUDE.md: a gate that reads the store must
re-evaluate as it hydrates), and the specs drive them with plain objects.

  stage metadata       stageMeta(status)            label, tag type and icon of a stage (6 stages)
  the five steps       STEPS, stepState(rta)        the current step and a status for each (decision 154)
  name + reference     rtaReference(id), referenceOf(rta), rtaTitle(rta)
  phase                phaseOf(rta), allOptionsDeclined(rta), approvedChangeParty(rta), approvedChanged(rta), approvedByPartyOf(rta)                 'draft' | 'review' (state.rta.phase)
  whose turn           waitingOn, isMyTurn, yourTurnTask
  the option summary   optionSummary(row)           the list's one-line summary
  option statuses      optionStatusMeta(status)     tag type and icon of an option status (6 statuses)
  review layer         responseChanged(option)      "changed since submitted" (DEV-9)
  who may do what      isRtaClient, isTcEditor, isTcCloser, isTcOptionDeleter, isTcResponder, isTcManager, isTcRequestManager,
                       canRespond, isTowerCoUser, isViewOnlyUser
  Steps 1 to 3        approvedKnown, proposedToOf, proposeTarget, canPropose, canBackToDraft, addSendsBackToDraft, addOptionReason,
                       canDeleteOption, deleteBlockedReason, canEditOptionDetails, optionDetailsLock,
                       canClientEditClientRef, noOptions
  the request (Step 1) hasBeenProposed, requestLocked, canEditRequestField, canEditRequest, requestLockReason
                       (decisions 157, 173): who may edit a request field, Site Name and Site ID included, and why not
  the review section   canEditReview, canSubmitReview, otherApprovedNo, submitReversesApproval,
                       submitUndoesDecision, stageAfterApprovalLeaves, localResponseChanged, decisionStands
  Step 4               canDeclineApproved, canRejectRta, isDecided, canDecide, canUnlockDecision,
                       canFinalize, mailOn, emailButton, decisionEmailed, decisionUnsent, step3Action
  Step 5 (Finalize)    isFinalize, canEditCompletion, canComplete
  after completion     siteDeleted(rta)             decision 57: the site the RTA created is gone
  list + header chip   showsChangedChip(rta, towerCo)   the Tower Co's "Changed since submitted"
  open RTA on a site   hasOpenRta(site)             DEV-31: a missing key is "no open RTA"

The contract is the paired API plan (xolas-tms-api docs/architecture/xol-67/plan.md) as revised by the v2 asks
(docs/plans/2026-10-27-xol-67-rta-v2.md, API-1 to API-24), the v2.1 delta (docs/plans/2026-10-28-xol-67-rta-v2-1-delta.md, API-26 to
API-41) and the SPA plan docs/plans/2026-10-25-xol-67-rta-rts-continuity.md (decisions 153 to 159 win over 135 to 152, which win over
1 to 134). The stored stages are v1's again: Searching and Client Review (the phases stay `draft` / `review`). The names of the API's answers are encoded HERE, in rtaOption and in
rtaRefusal and nowhere else, so a rename on the API side is one edit (plan risk R1).

The row an RTA is read from is EITHER a list row (`vw_actions`) or `GET /actions/{id}`; both carry the same FLAT keys
(API-14, DEV-66): optionCount, liveOptionCount, draftCount, inReviewCount, declinedCount, approvedOptionNo, approvedOptionId,
approvedChangedByParty, finalOptionNo, phase, proposedTo, draftParty, rtaName, rtaReference, sendMail, and, for information only,
changedResponseCount / changedOptionNo / changedByParty (they count EVERY option; P-S1: only the APPROVED option's unsubmitted
change moves the cue and blocks the decision, so those three are never read for a gate). A full action also carries `state.rta` (the same phase / proposedTo / draftParty, the name, the Tower Co decision
`tc`, `lastReset`, `closedFromStage`, `firstProposedAt`) and `state.nominal` (the REQUEST: its point, locality and requirements,
decision 153; lib/rtaNominal).

Roles are functions of (rta, org, profile): `org` is the store's organisation, `profile` the
signed-in contact ({ orgCategory, manager, super, userRole, viewer, organisation_id }). They
mirror AccessMixins (isTowerCo / isSuper / isManager / canAccess) without a component, and
the server checks the same rules (RtaRoles). The SPA is a courtesy; the API decides.

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

import { normaliseOrgCategory } from '@/lib/contactRoles'
import { hasRemarksText } from '@/lib/remarksText'

/* ------------------------------------------------------------------------
   Stages (`actions.status` of an RTA), v2.1 delta 1.1 (the stored names are v1's again: Searching, Client Review)
------------------------------------------------------------------------ */

// key -> { label, type (el-tag; '' is the default blue), icon }.
var STAGES = {
  'Searching': { label: 'Searching', type: 'info', icon: 'fa-search' },
  'Client Review': { label: 'Client Review', type: 'warning', icon: 'fa-eye' },
  'Client Approved': { label: 'Client Approved', type: 'success', icon: 'fa-thumbs-up' },
  'Finalize': { label: 'Finalize', type: '', icon: 'fa-clipboard-check' },
  // `Closed` is "Completed" and `Rejected` is "ended by Reject RTA…": the API's two terminal values.
  'Closed': { label: 'Completed', type: 'success', icon: 'fa-flag' },
  'Rejected': { label: 'Rejected', type: 'danger', icon: 'fa-ban' }
}

export var STAGE_KEYS = Object.keys(STAGES)

var TERMINAL = ['Closed', 'Rejected']

function has (obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key)
}

function num (v) {
  var n = Number(v)
  return isNaN(n) ? 0 : n
}

function present (v) {
  return v !== undefined && v !== null && v !== ''
}

/**
 * { key, label, type, icon, terminal } of a stage. A status this app does not know is shown
 * as itself with the neutral look, never as a blank.
 */
export function stageMeta (status) {
  var key = present(status) ? String(status) : ''

  if (!has(STAGES, key)) {
    return { key: key, label: key || 'Unknown', type: 'info', icon: 'fa-question-circle', terminal: false }
  }

  var s = STAGES[key]

  return { key: key, label: s.label, type: s.type, icon: s.icon, terminal: TERMINAL.indexOf(key) !== -1 }
}

export function isTerminal (rta) {
  return !!rta && TERMINAL.indexOf(rta.status) !== -1
}

/* ------------------------------------------------------------------------
   Reading the RTA: state.rta, the flat keys, the name and the reference
------------------------------------------------------------------------ */

function rtaStateOf (rta) {
  var s = rta && rta.state

  return s && s.rta && typeof s.rta === 'object' ? s.rta : {}
}

// A key the API sends flat (list rows and the action, API-14) or inside `state.rta`; null for neither.
function keyOf (rta, key) {
  if (rta && present(rta[key])) {
    return rta[key]
  }

  var v = rtaStateOf(rta)[key]

  return present(v) ? v : null
}

/**
 * `state.rta.phase` (API-1): 'draft' | 'review' (the internal values; the stages are Searching and Client Review). A row that
 * does not carry it is read from the stage (Searching is the draft phase; Client Review, Client Approved and Finalize all hold the
 * review phase, the invariant of plan 2.1); a terminal RTA with no stored phase is ''.
 */
export function phaseOf (rta) {
  var p = keyOf(rta, 'phase')

  if (p === 'draft' || p === 'review') {
    return p
  }

  if (rta && rta.status === 'Searching') {
    return 'draft'
  }

  if (rta && (rta.status === 'Client Review' || rta.status === 'Client Approved' || rta.status === 'Finalize')) {
    return 'review'
  }

  return ''
}

/**
 * The RTA's reference (decision 138): 'RTA-' and the id zero-padded to six digits (`RTA-000918`); an id above 999999 prints
 * in full. '' for no id. Computed, never stored: the API projects it as `reference` and this is the same function for a row
 * that lacks it.
 */
export function rtaReference (id) {
  var n = Number(id)

  if (!present(id) || isNaN(n) || n <= 0 || Math.floor(n) !== n) {
    return ''
  }

  var digits = String(n)

  while (digits.length < 6) {
    digits = '0' + digits
  }

  return 'RTA-' + digits
}

/** Who the options were proposed to (`proposedTo`): 'owner' (the client) | 'towerco' | null. A Tower Co-client RTA is always 'owner' (I-21). */
export function proposedToOf (rta) {
  var v = keyOf(rta, 'proposedTo')

  return v === 'owner' || v === 'towerco' ? v : null
}

/**
 * Whose unsubmitted review change stands on the APPROVED option (P-S1, I-11): 'owner' | 'towerco' | null. The API's summary key
 * `approvedChangedByParty` is the `client_status_by_party` of the Approved option while its `responseChanged` is 1, else null. Only
 * this change moves the cue to its author, moves the stepper back to Review and blocks Submit decision; a change on another option
 * (a live, unsubmitted review in Review) does none of them.
 */
export function approvedChangeParty (rta) {
  var v = keyOf(rta, 'approvedChangedByParty')

  return v === 'owner' || v === 'towerco' ? v : null
}

/** Does the approved option carry an unsubmitted review change (P-S1)? */
export function approvedChanged (rta) {
  return approvedChangeParty(rta) !== null
}

/**
 * Who submitted the approval of the approved option: 'owner' | 'towerco' | null (the summary key `approvedByParty`, asked of the API for
 * DEV-66, and on the compact `rta` of an option row since DEV-89; null when none is approved). Wording that says WHO approved ("approved by Telco Beta", "You approved")
 * needs it; without it the sentence is neutral ("Option 2 is approved").
 */
export function approvedByPartyOf (rta) {
  var v = keyOf(rta, 'approvedByParty')

  return v === 'owner' || v === 'towerco' ? v : null
}

/** The reference of an RTA row: the API's `reference` / `rtaReference` when it sent one, else the function above. */
export function referenceOf (rta) {
  if (!rta) {
    return ''
  }

  if (present(rta.reference)) {
    return String(rta.reference)
  }

  if (present(rta.rtaReference)) {
    return String(rta.rtaReference)
  }

  return rtaReference(rta.id)
}

/**
 * The RTA's title (decisions 138, 158; API P-S25): the Site Name of the request, `state.nominal.siteName`: the generated name (a nearby
 * street or area, made once at create: `generatedNameFrom`) until a Tower Co manager changes it, until the RTA is Completed; then the site's
 * own name: the single read's `completion.site.siteName` (live), the list's `siteName` (`vw_actions`), else the saved Finalize value. The list's `rtaName` and the compact `rta`'s `name` are the same value; `state.rta.name`
 * is the v2 key, read only as a last resort. '' when there is none.
 */
export function rtaTitle (rta) {
  if (!rta) {
    return ''
  }

  // A Completed RTA is named by the site it created, LIVE: `completion.site.siteName` (API b8fc0d36; both the Tower Co and the client get
  // `completion.site`, null once the site is deleted), so a rename after Finalize shows on both sides.
  if (rta.status === 'Closed') {
    var live = rta.completion && rta.completion.site && typeof rta.completion.site === 'object' ? rta.completion.site.siteName : null

    if (present(live) && String(live).trim() !== '') {
      return String(live).trim()
    }
  }

  // The list view (`vw_actions`) has no `completion` but puts the site's name in `siteName`.
  if (rta.status === 'Closed' && present(rta.siteName)) {
    return String(rta.siteName)
  }

  // Before that: what Finalize saved for the site (`completion.values.site.siteName`, which Tower Co callers get), else the request's name below.
  if (rta.status === 'Closed') {
    var values = rta.completion && rta.completion.values && typeof rta.completion.values === 'object' ? rta.completion.values : {}
    var typed = values.site && typeof values.site === 'object' ? values.site.siteName : null

    if (present(typed) && String(typed).trim() !== '') {
      return String(typed).trim()
    }
  }

  var nominal = rta.state && rta.state.nominal && typeof rta.state.nominal === 'object' ? rta.state.nominal : {}

  if (present(nominal.siteName) && String(nominal.siteName).trim() !== '') {
    return String(nominal.siteName).trim()
  }

  var name = rtaStateOf(rta).name

  if (present(name)) {
    return String(name)
  }

  if (present(rta.rtaName)) {
    return String(rta.rtaName)
  }

  if (present(rta.name)) {
    return String(rta.name)
  }

  return present(rta.siteName) ? String(rta.siteName) : ''
}

/* ------------------------------------------------------------------------
   The five steps (decision 154) and the stepper's statuses
------------------------------------------------------------------------ */

export var STEPS = [
  { no: 1, name: 'Requested', icon: 'fa-map-pin' },
  { no: 2, name: 'Searching', icon: 'fa-search' },
  { no: 3, name: 'Client Review', icon: 'fa-eye' },
  { no: 4, name: 'Tower Co Decision', icon: 'fa-handshake' },
  { no: 5, name: 'Finalize', icon: 'fa-flag' }
]

/** "Client Review" of step 3, or '' for a number that is not a step. */
export function stepName (n) {
  var s = STEPS[num(n) - 1]
  return s ? s.name : ''
}

/**
 * The five steps as the Activity log's Step filter takes them: [{ stepNo, partyNo: 1, stepName }]
 * (requestActivity.activityStepOptions lists party-1 steps only). An RTA row carries no steps of
 * its own, so this is its `steps`.
 */
export function activitySteps () {
  return STEPS.map(function (s) { return { stepNo: s.no, partyNo: 1, stepName: s.name } })
}

// [status of step 1 ... 5] per stage: 's' success, 'p' process, 'e' error, 'w' wait.
var STATUS_WORD = { s: 'success', p: 'process', e: 'error', w: 'wait' }

// stage -> [current step, pattern]. Delta 1.1: Step 1 (Requested) is never a current stage, the request exists from create on, so it is
// always success; the Client Approved split is made in stepState.
var STEP_TABLE = {
  'Searching': [2, 'spwww'],
  'Client Review': [3, 'sspww'],
  'Client Approved': [4, 'ssspw'],
  'Finalize': [5, 'ssssp'],
  'Closed': [5, 'sssss']
}

// A Rejected RTA stands on the step it was rejected from (`closedFromStage`, Reject RTA… is offered in Searching, Client Review and Client Approved).
var REJECTED_FROM = { 'Searching': 2, 'Client Review': 3, 'Client Approved': 4 }
var REJECTED_PATTERN = { 2: 'sewww', 3: 'sseww', 4: 'sssew' }

/**
 * Where an RTA is on the stepper: { current: 2-5, statuses: [5 x 'success'|'process'|'error'|'wait'],
 * terminal: null | 'completed' | 'rejected' }.
 *
 * `current` is the step the drawer opens on. Step 1 (Requested) is never current: the request exists from create on, so it is
 * always success (decision 153). A Completed RTA is "all done": current 5, every step success. A Rejected one stays on the step it
 * was rejected from (`closedFromStage`: Searching 2, Client Review 3, Client Approved 4; an RTA with none reads as 4), that step in
 * error, the later ones waiting, and the caller words step 5 "Not reached" (`terminal === 'rejected'`). Client Approved sits on
 * step 4 (deciding is the Tower Co's turn) unless the APPROVED option has an unsubmitted change (`approvedChangedByParty`, P-S1),
 * because the side that changed it has to submit first: then the cursor goes back to step 3, as v1 did. A status this app does not
 * know reads as Searching.
 */
export function stepState (rta) {
  var status = rta && present(rta.status) ? String(rta.status) : 'Searching'

  if (status === 'Rejected') {
    var from = keyOf(rta, 'closedFromStage')
    var at = from !== null && has(REJECTED_FROM, String(from)) ? REJECTED_FROM[String(from)] : 4

    return {
      current: at,
      statuses: REJECTED_PATTERN[at].split('').map(function (c) { return STATUS_WORD[c] }),
      terminal: 'rejected'
    }
  }

  var key = has(STEP_TABLE, status) ? status : 'Searching'
  var entry = STEP_TABLE[key]
  var current = entry[0]
  var pattern = entry[1]

  if (key === 'Client Approved' && approvedChanged(rta)) {
    current = 3
    pattern = 'sspww'
  }

  return {
    current: current,
    statuses: pattern.split('').map(function (c) { return STATUS_WORD[c] }),
    terminal: key === 'Closed' ? 'completed' : null
  }
}

/**
 * The furthest step the drawer can OPEN (decision 162). Normally the current step (`stepState().current`). The exception: while the RTA is in Client
 * Approved, Step 4 stays openable even when the cursor was sent back to step 3 by an unsubmitted change of the approved option (`approvedChangedByParty`),
 * so the Tower Co can see Submit decision disabled with "{Telco} changed its review of option n …" (M-21b). The cursor (the stepper, `waitingOn`) is
 * unchanged; only what can be opened widens.
 */
export function furthestStep (rta) {
  var current = stepState(rta).current

  return rta && rta.status === 'Client Approved' && current < 4 ? 4 : current
}

/* ------------------------------------------------------------------------
   Whose turn it is (API-1 `mine`)
------------------------------------------------------------------------ */

function clientIdOf (rta) {
  if (!rta) return 0
  if (present(rta.organisation_id)) return num(rta.organisation_id)
  if (rta.client && present(rta.client.id)) return num(rta.client.id)
  return num(rta.client_id)
}

function orgIdOf (org, profile) {
  if (org && present(org.id)) return num(org.id)
  return num(profile && profile.organisation_id)
}

function isTowerCoOrg (org) {
  return !!org && normaliseOrgCategory(org.category) === 'Tower Company'
}

// 'owner' (the client's side) -> 'client'; 'towerco' -> 'towerco'; anything else -> the fallback.
function partyOf (value, fallback) {
  return value === 'owner' ? 'client' : (value === 'towerco' ? 'towerco' : fallback)
}

/**
 * Searching with no Draft option left and some Declined: every option was declined and the RTA went back to Searching (I-1, 142).
 * The proposer is waiting to add an option. Needs the row's `draftCount` (API-14): without the key it is never claimed.
 */
export function allOptionsDeclined (rta) {
  return !!rta && rta.status === 'Searching' && present(rta.draftCount) && num(rta.draftCount) === 0 && num(rta.declinedCount) > 0
}

/**
 * A Searching RTA with no options at all (decisions 153, 155: create makes none, and Option 1 can be deleted, so zero options is a normal
 * Searching state). Needs the row's `optionCount` (API-14): without the key it is never claimed, so a compact `rta` is not read as empty.
 */
export function noOptions (rta) {
  return !!rta && rta.status === 'Searching' && present(rta.optionCount) && num(rta.optionCount) === 0
}

/**
 * Who the RTA is waiting on (API-1): { party: 'client' | 'towerco' | null, mine, ending }.
 *
 *   Searching                             the side holding the search (`draftParty`: the Tower Co after a create; the proposer after an auto-return)
 *   Client Review                         the side the options were proposed to (`proposedTo`)
 *   Client Approved, a changed review     the side that made the change to the approved option (`approvedChangedByParty`, P-S1)
 *   Client Approved                       the Tower Co (decide)
 *   Finalize                              the Tower Co
 *   Completed / Rejected                  nobody (party null)
 *   a Tower Co-client RTA, any open stage the Tower Co (it is the client too)
 *
 * `mine` is whether `org` is that party (the client: the org is the RTA's client; the Tower
 * Co: the org is a Tower Company). `ending` is what follows "Waiting on: {org}".
 */
export function waitingOn (rta, org) {
  if (!rta || isTerminal(rta)) {
    return { party: null, mine: false, ending: '' }
  }

  var party = 'towerco'
  var ending = ''
  var no = num(rta.approvedOptionNo)

  if (rta.status === 'Searching') {
    party = partyOf(keyOf(rta, 'draftParty'), 'towerco')
    // UX spec 4.1: no options at all "to add options"; every option declined "to add an option"; otherwise the client is waited on "to propose
    // options" and the Tower Co "to propose the options".
    ending = noOptions(rta) ? ' to add options' : (allOptionsDeclined(rta) ? ' to add an option' : (party === 'towerco' ? ' to propose the options' : ' to propose options'))
  } else if (rta.status === 'Client Review') {
    party = partyOf(keyOf(rta, 'proposedTo'), 'client')
    ending = ' to review the options'
  } else if (rta.status === 'Client Approved') {
    if (approvedChanged(rta)) {
      party = partyOf(approvedChangeParty(rta), 'client')
      ending = ' to submit its changed review'
    } else {
      ending = no > 0 ? ' to decide on option ' + no : ' to decide'
    }
  } else if (rta.status === 'Finalize') {
    ending = ' to finalize the site'
  }

  if (clientIsTowerCo(rta, org)) {
    party = 'towerco'
  }

  var mine = party === 'client'
    ? orgIdOf(org) > 0 && orgIdOf(org) === clientIdOf(rta)
    : isTowerCoOrg(org)

  return { party: party, mine: mine, ending: ending }
}

/**
 * Is it this organisation's turn (the list's "Your turn" tag and the Needs My Action bucket)? It is waitingOn's `mine`, which
 * is the API's `mine` bucket rule (API-1). Org-level on purpose: the Tower Co may answer for the client (76), but "waiting on
 * {Telco}" stays the client's.
 */
export function isMyTurn (rta, org) {
  if (!rta || isTerminal(rta)) {
    return false
  }

  return waitingOn(rta, org).mine
}

/**
 * The one task a "Your turn" tag is followed by, or null when it is not this organisation's turn (UX spec 4.1).
 *
 * @param {Object} names { telco, towerCo } the client's name and the Tower Co's
 */
export function yourTurnTask (rta, org, names) {
  if (!isMyTurn(rta, org)) {
    return null
  }

  names = names || {}

  var telco = names.telco || 'the client'
  var towerCo = names.towerCo || 'the Tower Co'
  var tcViewer = isTowerCoOrg(org)
  var tcClient = clientIsTowerCo(rta, org)

  switch (rta.status) {
    case 'Searching':
      if (allOptionsDeclined(rta)) {
        return 'Every option was declined. Add, or delete, options and propose again.'
      }

      if (noOptions(rta)) {
        return tcClient ? 'Add options and propose them.' : 'Add options and propose them to ' + (tcViewer ? telco : towerCo) + '.'
      }

      // A Tower Co client is the proposer too: "to {itself}" would name it twice (I-21).
      return tcClient ? 'Propose the options.' : 'Propose the options to ' + (tcViewer ? telco : towerCo) + '.'
    case 'Client Review':
      if (tcClient) {
        return 'Review the options.'
      }

      if (tcViewer) {
        var n = num(rta.inReviewCount)

        return n > 0
          ? 'Review ' + n + ' ' + (n === 1 ? 'option' : 'options') + ' from ' + telco + '.'
          : 'Review the options from ' + telco + '.'
      }

      return 'Review the options from ' + towerCo + '.'
    case 'Client Approved':
      if (approvedChanged(rta)) {
        return 'Submit your changed review of option ' + num(rta.approvedOptionNo) + '.'
      }

      return 'Decide on option ' + num(rta.approvedOptionNo) + '.'
    case 'Finalize':
      return tcClient
        ? 'Fill in the new site, then complete.'
        : 'Fill in the new site and the RTS, then complete.'
  }

  return null
}

// A Tower Co client: the RTA's client is the Tower Co itself, so there is no RTS (decision 4).
function clientIsTowerCo (rta, org) {
  if (rta && rta.client && present(rta.client.category)) {
    return normaliseOrgCategory(rta.client.category) === 'Tower Company'
  }

  return isTowerCoOrg(org) && orgIdOf(org) === clientIdOf(rta)
}

/**
 * Who a Propose goes to, from the caller's side (I-21): 'client' when the caller is the Tower Co (it proposes to the RTA's
 * client), 'towerco' when the caller is the client, 'self' for a Tower Co-client RTA (the Tower Co is both, nobody is e-mailed
 * and the button names no side).
 */
export function proposeTarget (rta, org) {
  if (clientIsTowerCo(rta, org)) {
    return 'self'
  }

  return isTowerCoOrg(org) ? 'client' : 'towerco'
}

/* ------------------------------------------------------------------------
   The option summary of a list row (UX spec 4.7)
------------------------------------------------------------------------ */

/**
 * The option summary of a row, from the flat keys (API-14): the numbers, normalised to numbers
 * (a missing key is 0), and `text`, the one line the Acquire list shows:
 *
 *   No options yet           (a Rejected RTA with none: "No options")
 *   Searching   {n} options, none proposed | {n} options · {d} declined (every option declined)
 *   Client Review  {n} options · {r} in Review [· {d} declined]
 *   Client Approved / Finalize   {n} options · option {k} approved by {Telco | Tower Co} (`approvedByParty`; "option {k} approved" without it)
 *   Completed    option {k} became the site
 *   Rejected     {n} options · none selected
 *
 * "1 option" is singular. A Searching RTA with some Declined options keeps "none proposed" and gains " · {d} declined".
 *
 * @param {Object} names { telco, towerCo } the client's name and the Tower Co's; "the client" / "the Tower Co" without them
 */
export function optionSummary (row, names) {
  row = row || {}
  names = names || {}

  var telco = names.telco || 'the client'
  var towerCo = names.towerCo || 'the Tower Co'
  var out = {
    optionCount: num(row.optionCount),
    liveOptionCount: num(row.liveOptionCount),
    draftCount: num(row.draftCount),
    inReviewCount: num(row.inReviewCount),
    declinedCount: num(row.declinedCount),
    approvedOptionNo: num(row.approvedOptionNo) || null,
    approvedOptionId: present(row.approvedOptionId) && num(row.approvedOptionId) > 0 ? num(row.approvedOptionId) : null,
    approvedByParty: approvedByPartyOf(row),
    approvedChangedByParty: approvedChangeParty(row),
    // For information only (P-S1): they count every option, not the approved one.
    changedResponseCount: num(row.changedResponseCount),
    changedOptionNo: num(row.changedOptionNo) || null,
    changedByParty: row.changedByParty === 'owner' || row.changedByParty === 'towerco' ? row.changedByParty : null,
    finalOptionNo: num(row.finalOptionNo) || null
  }
  var n = out.optionCount
  var options = n + ' ' + (n === 1 ? 'option' : 'options')
  var declined = out.declinedCount > 0 ? ' · ' + out.declinedCount + ' declined' : ''
  var text

  if (n === 0) {
    // "yet" would promise more to a closed RTA: a Rejected one that never had an option (v2.1 allows it, decision 155) just has none.
    out.text = row.status === 'Rejected' ? 'No options' : 'No options yet'
    return out
  }

  if (row.status === 'Closed' && out.finalOptionNo) {
    text = 'option ' + out.finalOptionNo + ' became the site'
  } else if (row.status === 'Rejected') {
    text = options + ' · none selected'
  } else if ((row.status === 'Client Approved' || row.status === 'Finalize') && out.approvedOptionNo) {
    // WHO approved needs `approvedByParty`; without it the line is neutral rather than a guess.
    text = options + ' · option ' + out.approvedOptionNo + ' approved' +
      (out.approvedByParty === 'owner' ? ' by ' + telco : (out.approvedByParty === 'towerco' ? ' by ' + towerCo : ''))
  } else if (row.status === 'Client Approved' || row.status === 'Finalize' || row.status === 'Closed') {
    // The approved (or final) option's number did not come with the row: say how many options there are, nothing more.
    text = options + declined
  } else if (row.status === 'Client Review') {
    text = options + (out.inReviewCount > 0 ? ' · ' + out.inReviewCount + ' in Review' : '') + declined
  } else if (allOptionsDeclined(row)) {
    text = options + declined
  } else {
    text = options + ', none proposed' + declined
  }

  out.text = text

  return out
}

/* ------------------------------------------------------------------------
   Option statuses (`site_candidates.optionStatus`, plan 2.3; UX spec section 1)
------------------------------------------------------------------------ */

// status -> { type (el-tag; '' is the default blue), icon }. The label is the stored value.
var OPTION_STATUSES = {
  'Draft': { type: 'info', icon: 'fa-pencil-alt' },
  'Review': { type: 'warning', icon: 'fa-eye' },
  'Approved': { type: 'success', icon: 'fa-thumbs-up' },
  'Declined': { type: 'danger', icon: 'fa-times-circle' },
  'Final Approved': { type: 'success', icon: 'fa-check-double' },
  'Not Selected': { type: 'info', icon: 'fa-minus-circle' }
}

export var OPTION_STATUS_KEYS = Object.keys(OPTION_STATUSES)

/** { status, label, type, icon } of an option status; an unknown one keeps its text with the neutral look. */
export function optionStatusMeta (status) {
  var key = present(status) ? String(status) : ''

  if (!has(OPTION_STATUSES, key)) {
    return { status: key, label: key || 'Unknown', type: 'info', icon: 'fa-question-circle' }
  }

  return { status: key, label: key, type: OPTION_STATUSES[key].type, icon: OPTION_STATUSES[key].icon }
}

/** At most this many live options per RTA (decision 60 / plan 1.2). */
export var LIVE_OPTION_LIMIT = 5

// Declined and Not Selected do not count toward the limit.
var NOT_LIVE = ['Declined', 'Not Selected']

export function liveOptionCount (options) {
  return (options || []).filter(function (o) {
    return !!o && NOT_LIVE.indexOf(o.optionStatus) === -1
  }).length
}

// A review can be set and submitted on these (API-7): the statuses `responseChanged` is about.
var RESPONDABLE = ['Review', 'Approved', 'Declined']

// Remarks as text, for the comparison only (the server compares after HtmlToText and trimming).
function remarksPlain (html) {
  return String(present(html) ? html : '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/[\s\u00a0]+/g, ' ').trim()
}

function clientStatusOf (v) {
  return present(v) ? String(v) : 'Pending'
}

/**
 * "Changed since submitted" for one option (plan 2.6; API DEV-9 as API-7 changes it). The SERVER computes it and sends
 * `responseChanged`; this returns that when it is a boolean, and only otherwise derives the same rule from the layers
 * (advisory, for a row that came without the key):
 *
 *   - only a Review, Approved or Declined option can be changed;
 *   - a review with NO submission yet (`submitted_at` empty): the live Review Status is not Pending. Remarks alone do not
 *     count until the first Submit;
 *   - once there is a submission: the live status differs from the submitted one, or the remarks text does.
 *
 * (`resubmitRequired` is gone, decision 50; the wire values are Pending, Approved and Declined, I-26.)
 */
export function responseChanged (option) {
  if (!option) {
    return false
  }

  if (typeof option.responseChanged === 'boolean') {
    return option.responseChanged
  }

  if (option.responseChanged === 0 || option.responseChanged === 1) {
    return option.responseChanged === 1
  }

  if (RESPONDABLE.indexOf(option.optionStatus) === -1) {
    return false
  }

  if (!present(option.submitted_at)) {
    return clientStatusOf(option.clientStatus) !== 'Pending'
  }

  return clientStatusOf(option.clientStatus) !== clientStatusOf(option.submittedStatus) ||
    remarksPlain(option.clientRemarks) !== remarksPlain(option.submittedRemarks)
}

/**
 * Does the SERVER say this option's review changed since it was submitted (`responseChanged`: true or 1)? Unlike responseChanged it never
 * derives the answer from the option's layers: the Submit gates compare the screen's `live` values themselves (localResponseChanged) and
 * use this only for a saved change the screen has not seen. It reads 0 / 1 as well as booleans (the API sends either).
 */
export function serverResponseChanged (option) {
  return !!option && (option.responseChanged === true || option.responseChanged === 1)
}

// What an option deleter may delete (API-18): a Draft or a Declined option, and only while the RTA is in Searching.
var DELETABLE = ['Draft', 'Declined']

/* ------------------------------------------------------------------------
   Roles. Plain functions of (rta, org, profile); see the header.
------------------------------------------------------------------------ */

function profileOf (profile) {
  return profile || {}
}

// AccessMixins.isTowerCo: the CONTACT's `orgCategory` is already normalised (the API strips the
// Xolas-Client marker from it), so it is compared as is, exactly as the mixin does.
function isTowerCoProfile (profile) {
  return profileOf(profile).orgCategory === 'Tower Company'
}

// AccessMixins.isSuper: a Tower Co user with the `super` flag or the legacy `super` role.
function isSuperProfile (profile) {
  var p = profileOf(profile)

  return isTowerCoProfile(p) && !!(p.super || p.userRole === 'super')
}

function isViewOnlyProfile (profile) {
  return !!profileOf(profile).viewer
}

// XOL-67 decision 92 (and API DEV-51): a non-Tower Co org has ONE account type, so a client contact is never "view-only", whatever
// stray `viewer` flag it carries (decision 97). View-only is a Tower Co concept, and the RTA's client gates only ask it of a
// Tower Co contact. (The app-wide version of this rule, AccessMixins.isViewOnly and the API's viewonly.gate, is XOL-680.)
function isTcViewOnly (profile) {
  return isTowerCoProfile(profile) && isViewOnlyProfile(profile)
}

// AccessMixins.isManager(group): a super, or a manager whose userRole intersects `group`
// (canAccess: `userRole` split on '|'; a contact with none falls back to its org category).
function isManagerOf (profile, group) {
  var p = profileOf(profile)

  if (isSuperProfile(p)) {
    return true
  }

  if (!p.manager) {
    return false
  }

  var role = p.userRole || String(p.orgCategory || '').toLowerCase()
  var roles = String(role).split('|')
  var wanted = group.split('|')

  return roles.some(function (r) { return wanted.indexOf(r) !== -1 })
}

/** Is the signed-in contact a Tower Co user (AccessMixins.isTowerCo)? View-only users included. */
export function isTowerCoUser (profile) {
  return isTowerCoProfile(profile)
}

/** Is the signed-in contact view-only? A Tower Co concept only (decision 92): a client contact never is. */
export function isViewOnlyUser (profile) {
  return isTcViewOnly(profile)
}

/**
 * The client: any contact of the RTA's client organisation (decisions 2, 29, 92). No category test
 * and NO super arm: a super user is the client only when the client is its own organisation. A
 * client org has one account type, so view-only, manager and role are never tested for a client
 * contact; a view-only Tower Co contact is not the client even when the client is a Tower Co (it is
 * a Tower Co contact, and the Tower Co's view-only rule applies to it).
 */
export function isRtaClient (rta, org, profile) {
  if (isTcViewOnly(profile)) {
    return false
  }

  var mine = orgIdOf(org, profileOf(profile))

  return mine > 0 && mine === clientIdOf(rta)
}

/** TC editor: a Tower Co manager of project, maintenance, management or property. One half of the responder gate (isTcResponder). */
export function isTcEditor (rta, org, profile) {
  return isTowerCoProfile(profile) && !isViewOnlyProfile(profile) && isManagerOf(profile, 'project|maintenance|management|property')
}

/** TC closer: a Tower Co manager of project, finance or admin (create for a client, decide, Submit decision, Decline, Finalize, Complete, Reject RTA…). */
export function isTcCloser (rta, org, profile) {
  return isTowerCoProfile(profile) && !isViewOnlyProfile(profile) && isManagerOf(profile, 'project|finance|admin')
}

/** TC option deleter: a Tower Co manager of project, property, finance, admin or management (decision 30). */
export function isTcOptionDeleter (rta, org, profile) {
  return isTowerCoProfile(profile) && !isViewOnlyProfile(profile) && isManagerOf(profile, 'project|property|finance|admin|management')
}

/**
 * Any Tower Co manager, whatever the group, and not view-only (decision 144: "managers exist only in Tower Co orgs"; a
 * super counts). A Telco has no manager accounts.
 *
 * This is NOT the gate of the request. It was written to be (decision 157, from the first proposal; 158 Q3, Site Name / Site ID at any time), but the gate is
 * `isTcRequestManager` (a responder who also has the manager flag), because the API answers 403 `not_allowed` to a manager outside the responder groups, and
 * since decision 173 Site Name and Site ID follow the same rule as every other request field (`canEditRequestField`). No file in `src/` calls this function
 * any more; it is only exported, and `rtaStage.spec.js` pins it.
 */
export function isTcManager (profile) {
  var p = profileOf(profile)

  return isTowerCoProfile(p) && !isViewOnlyProfile(p) && (isSuperProfile(p) || !!p.manager)
}

/**
 * The Tower Co "manager" of the request (decisions 157, 158 Q3; API P-S27, 2026-10-28): the contact who may change the request once it has been
 * proposed (Site Name and Site ID included: decision 173 made them ordinary request fields, not a manager-only pair). The API's rule is "a D98 responder
 * who also has the manager flag, not view-only"; a responder is always a manager already (isManagerOf needs the flag, or `super`, and excludes a view-only user),
 * so it is isTcResponder. A manager with no responder role (a group outside project, finance, admin, maintenance, management, property) is neither, and the API answers 403 not_allowed.
 */
export function isTcRequestManager (rta, org, profile) {
  return isTcResponder(rta, org, profile)
}

/**
 * TC responder (decisions 76, 98, 148; API DEV-36, P-C2): the Tower Co user who may add an option, set and submit a review for the
 * client (or in its own right when the Telco proposed to it), and edit an option. Carlos: "All who can create an RTA or site option", so it
 * is the two Tower Co gates joined, not a new role: the Create RTA gate (`isTcCloser`: project, finance, admin) OR the editor gate
 * (`isTcEditor`: project, maintenance, management, property). Decision 148 made the + Option gate this same responder gate (it was the
 * editor gate alone). Both exclude a view-only Tower Co user.
 */
export function isTcResponder (rta, org, profile) {
  return isTcCloser(rta, org, profile) || isTcEditor(rta, org, profile)
}

/** May this caller set and submit a review (decision 98)? The client, or a Tower Co responder. */
export function canRespond (rta, org, profile) {
  return isRtaClient(rta, org, profile) || isTcResponder(rta, org, profile)
}

function tcOf (rta) {
  var tc = rtaStateOf(rta).tc

  return tc && typeof tc === 'object' ? tc : {}
}

/** The Tower Co decision, never null: the stored `state.rta.tc`, or an empty one (Pending, unlocked). */
export function tcDecision (rta) {
  var tc = tcOf(rta)

  return {
    approvedStatus: tc.approvedStatus || null,
    optionId: present(tc.optionId) ? tc.optionId : null,
    approved_at: tc.approved_at || null,
    remarks: tc.remarks || null,
    approvalLocked: !!tc.approvalLocked,
    decisionMail: tc.decisionMail || null
  }
}

/** Is the Tower Co decision locked (Submit decision locks an approval; the RTA is in Finalize)? */
export function isDecisionLocked (rta) {
  return !!tcOf(rta).approvalLocked
}

/**
 * May this option be deleted (decisions 30, 62, 155; API-18)? Only while the RTA is in Searching (otherwise the API answers `wrong_phase`),
 * and either a Tower Co option deleter deletes a Draft or a Declined option, or the client deletes a Draft its own org added
 * (`added_by_party` 'owner'). Option 1 is an ordinary option (decision 155 reverses 132): it follows these rules like every other, and the
 * RTA may reach zero options.
 */
export function canDeleteOption (option, rta, org, profile) {
  if (!option || !rta || isTerminal(rta) || rta.status !== 'Searching') {
    return false
  }

  if (isTcOptionDeleter(rta, org, profile)) {
    return DELETABLE.indexOf(option.optionStatus) !== -1
  }

  return isRtaClient(rta, org, profile) && option.optionStatus === 'Draft' && option.added_by_party === 'owner'
}

// Why the client's delete button of its own option is disabled (deleteBlockedReason).
export var DELETE_BLOCKED_REVIEW = 'review'

/**
 * Why is the client's Delete of an option NOT available, where the button is shown disabled (decision 122, mapped by I-25)? ''
 * when there is nothing to say: the caller may delete it (canDeleteOption), or the button is not drawn at all (a Tower
 * Co deleter, an option the Tower Co added, a closed RTA, any stage but Client Review, a status with no useful thing to tell the client).
 *   'review'  the client's own option is in Review (the RTA is in Client Review): Back to Searching first
 */
export function deleteBlockedReason (option, rta, org, profile) {
  if (!option || !rta || isTerminal(rta) || isTcOptionDeleter(rta, org, profile)) {
    return ''
  }

  if (!isRtaClient(rta, org, profile) || option.added_by_party !== 'owner') {
    return ''
  }

  return rta.status === 'Client Review' && option.optionStatus === 'Review' ? DELETE_BLOCKED_REVIEW : ''
}

// Statuses a content PUT is refused on (API-6: 409 option_locked). Nobody edits an option's content any more once it is one of these.
var CONTENT_LOCKED = ['Final Approved', 'Not Selected']

// The submitted review status that locks an option's details (decision 126, mapped): once an Approved or a Declined is SUBMITTED.
var SUBMITTED_LOCKS = ['Approved', 'Declined']

/**
 * Has a review of Approved or Declined been submitted on this option (decision 126)? Returns the submitted status that locks the
 * option's details, else ''. It reads the SUBMITTED layer (`submittedStatus`), never the live Review Status: a live choice that is
 * not submitted yet locks nothing, and a re-submit as Pending reopens the details.
 */
export function submittedLockOf (option) {
  var s = option && present(option.submittedStatus) ? String(option.submittedStatus) : ''

  return SUBMITTED_LOCKS.indexOf(s) !== -1 ? s : ''
}

/**
 * The one gate for an option's DETAILS (decisions 125, 126; API-6). Details are open to both sides, in ANY open stage and
 * option status, until a review of Approved or Declined is submitted on the option. Closed for everyone when:
 *   - the RTA is closed (Completed or Rejected);
 *   - the RTA is in Finalize: every option is read-only from there (32 as changed);
 *   - the option is terminal (Final Approved, Not Selected);
 *   - a review of Approved or Declined was submitted on it (submittedLockOf). A Step 4 Decline writes both layers as Declined,
 *     so it locks too.
 * Otherwise: a Tower Co responder (isTcResponder, decision 98) or a contact of the client (decision 92: no view-only or
 * manager test for a client).
 *
 * `canEditOptionDetails` says whether the VIEWER may edit; `optionDetailsLock` says why a viewer who could, may not now:
 * { reason: 'closed' | 'finalize' | 'terminal' | 'submitted', status } or null. The field-level split stays DEV-10's: the Tower
 * Co-only fields (rtaOption.TC_ONLY_FIELDS) need a Tower Co responder, `clientRef` is the client's (canClientEditClientRef).
 */
export function optionDetailsLock (option, rta) {
  if (!option || !rta || isTerminal(rta)) {
    return { reason: 'closed', status: '' }
  }

  if (rta.status === 'Finalize') {
    return { reason: 'finalize', status: '' }
  }

  if (CONTENT_LOCKED.indexOf(option.optionStatus) !== -1) {
    return { reason: 'terminal', status: '' }
  }

  var submitted = submittedLockOf(option)

  return submitted ? { reason: 'submitted', status: submitted } : null
}

export function canEditOptionDetails (option, rta, org, profile) {
  if (optionDetailsLock(option, rta)) {
    return false
  }

  return isRtaClient(rta, org, profile) || isTcResponder(rta, org, profile)
}

/**
 * May the client edit its own reference (`clientRef`)? On its RTA's options while the RTA is open and not in Finalize, whatever the
 * status EXCEPT the terminal ones (the server refuses a content PUT on them with 409 `option_locked`, API-6). Its own predicate,
 * because the shared-fields rule above is narrower: a submitted review does not stop it.
 */
export function canClientEditClientRef (option, rta, org, profile) {
  if (!option || !rta || isTerminal(rta) || rta.status === 'Finalize' || CONTENT_LOCKED.indexOf(option.optionStatus) !== -1) {
    return false
  }

  return isRtaClient(rta, org, profile)
}

/* ------------------------------------------------------------------------
   The request, Step 1 (decisions 153, 157, 158): who may edit which field
------------------------------------------------------------------------ */

/**
 * Has this RTA ever been proposed (decision 157)? The API keeps a sticky `state.rta.firstProposedAt` for it (plan-v2 DEV-66; it survives Back
 * to Searching, and M6 sets it on converted RTAs). When the row carries the key it is the answer, and a key that is PRESENT but null means "never
 * proposed". Only when the key is MISSING (a row that did not come from the API, or an older answer: DEV-89 puts `firstProposedAt` on the compact `rta` of an option row too) does this fall back, and it FAILS SAFE:
 * proposed unless the stage is Searching AND nothing shows a proposal ever happened. Every stage after Searching was reached by a Propose; in
 * Searching the evidence is a proposal stamp (`proposedAt`, `proposedBy`) or a side it went to (`proposedTo`: create writes null, and Back to
 * Searching keeps it). `draftSince` is NOT evidence: DEV-66 writes it every time the RTA enters Searching, create included. Encoded here once
 * (plan risk R1).
 */
export function hasBeenProposed (rta) {
  if (!rta) {
    return false
  }

  if (has(rta, 'firstProposedAt') || has(rtaStateOf(rta), 'firstProposedAt')) {
    return present(has(rta, 'firstProposedAt') ? rta.firstProposedAt : rtaStateOf(rta).firstProposedAt)
  }

  if (rta.status === 'Client Review' || rta.status === 'Client Approved' || rta.status === 'Finalize' || rta.status === 'Closed') {
    return true
  }

  return ['proposedAt', 'proposedBy', 'proposedTo'].some(function (k) { return present(keyOf(rta, k)) })
}

/**
 * Is the request locked to Tower Co managers (decision 157)? From the first proposal on, and for good: Back to Searching and an add in
 * Client Review do not reopen it. It is hasBeenProposed under the request's own name. (The API refuses with 409 `request_locked {fields}`.)
 */
export function requestLocked (rta) {
  return hasBeenProposed(rta)
}

// The request is open to edits in any stage but Finalize and the two terminal ones (Finalize's site form sets the final location).
function requestOpen (rta) {
  return !!rta && !isTerminal(rta) && rta.status !== 'Finalize'
}

/**
 * May this caller edit ONE request field (decisions 157, 173)? `field` is a wire key of `state.nominal` (lib/rtaNominal.NOMINAL_FIELDS). Site Name and Site ID
 * are ordinary request fields since decision 173 (a Telco sets them at create, so it must be able to fix a typo): there is no field with a rule of its own.
 *
 *   every field, before the first proposal   the client's contacts and a Tower Co responder (D98: editor or closer)
 *   every field, from the first proposal     a Tower Co request manager only (isTcRequestManager; sticky: requestLocked)
 *   nobody, in Finalize, Completed or Rejected; a view-only user never
 *
 * A Tower Co request manager is `isTcRequestManager` (a responder with the manager flag, not view-only). For a Tower Co-client RTA every Tower Co contact is the
 * client (DEV-54), so its non-manager contacts lock with everyone else's. The API enforces the same rules; this is the courtesy.
 */
export function canEditRequestField (field, rta, org, profile) {
  if (!requestOpen(rta) || isTcViewOnly(profile)) {
    return false
  }

  if (requestLocked(rta)) {
    return isTcRequestManager(rta, org, profile)
  }

  // Before the first proposal: the client, or a Tower Co responder (the API refuses every other contact with 403 not_allowed, P-S27), so the gate
  // only ever NARROWS over time (decision 157): whoever may edit after the first proposal could edit before it.
  return isRtaClient(rta, org, profile) || isTcResponder(rta, org, profile)
}

/** May this caller edit the request? canEditRequestField for any field (they all follow one rule since decision 173). */
export function canEditRequest (rta, org, profile) {
  return canEditRequestField('', rta, org, profile)
}

/**
 * Why the request is NOT editable for this caller, for the lock note: '' when it is editable.
 *   'closed'     the RTA is Completed or Rejected
 *   'finalize'   Finalize's site form sets the final location, so the request is frozen from there
 *   'viewonly'   a view-only Tower Co user
 *   'proposed'   options have been proposed, so only a Tower Co manager may change the request
 *   'notallowed' not the client and not a Tower Co responder, before the first proposal
 */
export function requestLockReason (rta, org, profile) {
  if (!rta || isTerminal(rta)) {
    return 'closed'
  }

  if (rta.status === 'Finalize') {
    return 'finalize'
  }

  if (isTcViewOnly(profile)) {
    return 'viewonly'
  }

  if (requestLocked(rta)) {
    return isTcRequestManager(rta, org, profile) ? '' : 'proposed'
  }

  return isRtaClient(rta, org, profile) || isTcResponder(rta, org, profile) ? '' : 'notallowed'
}

/* ------------------------------------------------------------------------
   The option layer and Steps 1 and 2 (plan 2.4, 2.5, 2.6)
------------------------------------------------------------------------ */

/**
 * The RTA as the rest of this file reads it, from either shape it arrives in: a full action
 * (GET /actions/{id}, the `action` of a command's envelope: it has `state`) or the COMPACT `rta`
 * the API appends to an option row (`{ id, status, organisation_id, name, reference, phase, proposedTo, draftParty, nominal,
 * tc, lastReset, sendMail }`, API-22, API-29), which a layer that opens on an option alone has. Null for nothing. A full action is
 * returned as it is.
 *
 * The compact `rta` carries `firstProposedAt`, `approvedOptionNo`, `approvedOptionId` and `approvedByParty` since DEV-89 (API plan v2.1); it still has
 * no `approvedChangedByParty`. rtaOf reads each when present and passes it through. When one is missing (an older answer, a stand-in) the helpers
 * stay defensive and FAIL SAFE: hasBeenProposed (a missing `firstProposedAt`, not a null one) reads "proposed" unless the stage is Searching with no proposal evidence (the request stays locked to
 * non-managers), and approvedKnown says the "another option is approved" answer is unknown (the server's already_approved decides). V4's
 * option layer passes the FULL action where it has it (the RTA drawer's `actionData`), which carries every key.
 */
export function rtaOf (src) {
  if (!src || typeof src !== 'object') {
    return null
  }

  if (src.state && typeof src.state === 'object') {
    return src
  }

  var out = {}

  Object.keys(src).forEach(function (k) { out[k] = src[k] })

  var r = { tc: src.tc && typeof src.tc === 'object' ? src.tc : {}, lastReset: src.lastReset || null }

  ;['phase', 'proposedTo', 'draftParty', 'name', 'closedFromStage'].forEach(function (k) {
    if (present(src[k])) {
      r[k] = src[k]
    }
  })

  // `firstProposedAt` is copied even as null: a key that is present and null means "never proposed" (hasBeenProposed); a missing one is unknown.
  if (has(src, 'firstProposedAt')) {
    r.firstProposedAt = src.firstProposedAt
  }

  out.state = { rta: r }

  // API-29: the compact `rta` carries the request's point and locality as `nominal`, which a full action holds as `state.nominal`.
  if (src.nominal && typeof src.nominal === 'object') {
    out.state.nominal = src.nominal
  }

  if (typeof src.sendMail === 'boolean' && !out.emails) {
    out.emails = { sendMail: src.sendMail }
  }

  return out
}

// The RTA is open and not in Finalize: what the Steps 1 to 3 commands need.
function openBeforeFinalize (rta) {
  return !!rta && !isTerminal(rta) && rta.status !== 'Finalize'
}

/**
 * May this caller Propose Option(s) (API-3)? The client or a Tower Co responder, the RTA in Searching, and at least one Draft
 * option to propose (`draftCount`; a Searching RTA whose options are all Declined, or that has none, has none: add one first).
 */
export function canPropose (rta, org, profile, draftCount) {
  return !!rta && rta.status === 'Searching' && num(draftCount) > 0 && canRespond(rta, org, profile)
}

/**
 * May this caller press Back to Searching (API-4, I-7; the route stays /rta/back-to-draft)? The client or a Tower Co responder, the RTA in
 * Client Review (reverse an approval first). The request stays locked after it (decision 157).
 */
export function canBackToDraft (rta, org, profile) {
  return !!rta && rta.status === 'Client Review' && canRespond(rta, org, profile)
}

/** Does adding an option now send the RTA back to Searching (137.2)? In Client Review it does; the opener says so. */
export function addSendsBackToDraft (rta) {
  return !!rta && rta.status === 'Client Review'
}

/**
 * Does this row say which option is approved (`approvedOptionNo` present, even as null for "none")? The compact `rta` of an option row may
 * not (see rtaOf): then "another option is approved" is UNKNOWN, otherApprovedNo reads null, and Submit review is not blocked here (the
 * server answers `already_approved`). Pass the full action to know.
 */
export function approvedKnown (rta) {
  return !!rta && Object.prototype.hasOwnProperty.call(rta, 'approvedOptionNo')
}

/**
 * Does the approved option count against the one approval? The number of ANOTHER option that is Approved (so this option
 * cannot be approved too), or null (API-7 `already_approved {optionNo}`, I-10). Read from the RTA's `approvedOptionNo`.
 */
export function otherApprovedNo (option, rta) {
  var no = num(rta && rta.approvedOptionNo)

  if (!no || !option) {
    return null
  }

  if (present(rta.approvedOptionId) && present(option.id)) {
    return Number(rta.approvedOptionId) === Number(option.id) ? null : no
  }

  return Number(option.optionNo) === no ? null : no
}

/**
 * May this caller edit the LIVE review (Review Status + remarks, which auto-save) on this option (API-7)? A responder
 * (canRespond: the client, or a Tower Co responder, decisions 76, 98), the RTA in Client Review or Client Approved (in Searching the section
 * reads; from Finalize it is frozen), and an option that can still be reviewed (Review, Approved, Declined).
 */
export function canEditReview (option, rta, org, profile) {
  return !!option && !!rta && (rta.status === 'Client Review' || rta.status === 'Client Approved') &&
    canRespond(rta, org, profile) && RESPONDABLE.indexOf(option.optionStatus) !== -1
}

/**
 * Does the review the user sees on screen (`live`: { status, remarks }, possibly ahead of what the server has saved) differ from
 * what it last SUBMITTED? The same rule as the server's `responseChanged` applied to the local values: a review with no
 * submission yet counts only a non-Pending status; once submitted, a different status or different remarks TEXT counts.
 */
export function localResponseChanged (option, live) {
  if (!option) {
    return false
  }

  var merged = {
    optionStatus: option.optionStatus,
    submitted_at: option.submitted_at,
    submittedStatus: option.submittedStatus,
    submittedRemarks: option.submittedRemarks,
    clientStatus: live ? live.status : option.clientStatus,
    clientRemarks: live ? live.remarks : option.clientRemarks
  }

  return responseChanged(merged)
}

/**
 * May a responder press Submit review? It may edit the review (canEditReview), what is on screen differs from what it submitted
 * (localResponseChanged) or the server says so (`responseChanged`), and it is not a second approval: with another option
 * Approved a live Approved is refused (`already_approved`, I-10), so the button is off. Declined needs remarks, which the caller
 * checks at the press, never here.
 */
export function canSubmitReview (option, rta, org, profile, live) {
  if (!canEditReview(option, rta, org, profile)) {
    return false
  }

  var status = live ? live.status : option.clientStatus

  if (status === 'Approved' && otherApprovedNo(option, rta) !== null) {
    return false
  }

  return localResponseChanged(option, live) || serverResponseChanged(option)
}

/**
 * Does an unlocked Tower Co decision (Approved) stand on this RTA? A submit that takes the approval away clears it (decisions
 * 47, 51 as changed). Pending is not a decision, and there is no Rejected decision any more (142).
 */
export function decisionStands (rta) {
  if (!rta || isTerminal(rta) || isDecisionLocked(rta)) {
    return false
  }

  return tcDecision(rta).approvedStatus === 'Approved'
}

/**
 * Would this Submit review take the Approved option out of Step 4 (U-8)? The option is the submitted Approved one, the RTA is in
 * Client Approved, and the review on screen is not Approved. The RTA then goes back to Client Review (or Searching); the caller confirms first.
 */
export function submitReversesApproval (option, rta, live) {
  if (!option || !rta || rta.status !== 'Client Approved' || option.optionStatus !== 'Approved') {
    return false
  }

  var status = live ? live.status : option.clientStatus

  return clientStatusOf(status) !== 'Approved'
}

/**
 * Would this Submit review also clear a saved Tower Co decision (47 as changed: the undo happens at Submit, not on the live pick)?
 * It reverses the approval (submitReversesApproval) while an unlocked Approved decision stands (decisionStands). The confirm gains
 * a sentence for it.
 */
export function submitUndoesDecision (option, rta, live) {
  return submitReversesApproval(option, rta, live) && decisionStands(rta)
}

/**
 * Where the RTA goes when the approved option stops being approved (a reversal by Submit review, or the Step 4 Decline; 142 applied
 * to every reversal, plan 2.5): 'Client Review' when an option is still in Review afterwards, else 'Searching' (only Declined options
 * are left, I-1). `to` is what the option becomes: 'Pending' (it goes back to Review, so the RTA is in Client Review), 'Declined' or
 * 'Approved' (still approved: no change, '').
 */
export function stageAfterApprovalLeaves (rta, to) {
  if (to === 'Approved') {
    return ''
  }

  if (to === 'Pending') {
    return 'Client Review'
  }

  return num(rta && rta.inReviewCount) > 0 ? 'Client Review' : 'Searching'
}

/**
 * Decline the approved option in Step 4 (142, I-4): the TC closer, the RTA in Client Approved, the option the submitted Approved one.
 * The button is not drawn for anyone else.
 */
export function canDeclineApproved (option, rta, org, profile) {
  return !!option && !!rta && rta.status === 'Client Approved' && option.optionStatus === 'Approved' && isTcCloser(rta, org, profile)
}

/**
 * Reject RTA… (142, I-2, I-3): the TC closer, an open RTA in Searching, Client Review or Client Approved. Not from Finalize (Unlock first),
 * and not on a terminal RTA. The stage decides, not the step being viewed.
 */
export function canRejectRta (rta, org, profile) {
  return openBeforeFinalize(rta) && isTcCloser(rta, org, profile)
}

/**
 * Why the + Option button is disabled, in the order of the UX spec (section 2.3), or '' when it is enabled: view-only, a closed
 * RTA, a locked decision (Finalize), an approved option (Client Approved), the limit of 5 live options, anyone else (not the
 * client, not a TC editor). `liveCount` is the RTA's live options (Declined and Not Selected do not count).
 */
export function addOptionReason (rta, org, profile, liveCount, names) {
  names = names || {}

  var telco = names.telco || 'the client'
  var towerCo = names.towerCo || 'the Tower Co'

  if (isTcViewOnly(profile)) {
    return 'View-only users can\'t add options.'
  }

  if (!rta || isTerminal(rta)) {
    return 'This RTA is closed.'
  }

  if (rta.status === 'Finalize' || isDecisionLocked(rta)) {
    return 'The ' + towerCo + ' approval is locked. Unlock it to add options.'
  }

  if (rta.status === 'Client Approved') {
    var approved = num(rta.approvedOptionNo)

    return (approved > 0 ? 'Option ' + approved : 'An option') + ' is approved. Reverse its review to add options.'
  }

  if (num(liveCount) >= LIVE_OPTION_LIMIT) {
    return 'An RTA can have at most ' + LIVE_OPTION_LIMIT + ' live options. Delete or decline one to add another.'
  }

  // Decision 148 (P-C2): the Tower Co adds options as a RESPONDER (editor or closer), the same gate as Create RTA. A finance or admin manager,
  // who can create an RTA with Option 1, can add Option 2.
  if (!isRtaClient(rta, org, profile) && !isTcResponder(rta, org, profile)) {
    return 'Only ' + telco + ' or ' + towerCo + ' can add options.'
  }

  return ''
}

/**
 * The site a Completed RTA created has since been deleted (decision 57)? The API detaches the RTA
 * (`site_id` NULL) and records `state.rta.siteDeleted { at, by, site_id, siteName, siteId, rtsId }`;
 * `by` is the name of the user who deleted it (DEV-35). Null when it was not.
 */
export function siteDeleted (rta) {
  var d = rtaStateOf(rta).siteDeleted

  return d && typeof d === 'object' ? d : null
}

/**
 * The Tower Co's "Changed since submitted" chip: an open RTA in Client Approved with the approved option's review changed after it
 * was submitted (`approvedChangedByParty`, P-S1). It is the Tower Co's cue and never the client's, who is told to submit instead. The list row and the drawer
 * header share it.
 *
 * @param {Object} rta the action or a list row (flat summary keys)
 * @param {boolean} towerCo is the viewer a Tower Co user
 */
export function showsChangedChip (rta, towerCo) {
  return !!towerCo && !!rta && !isTerminal(rta) && rta.status === 'Client Approved' && approvedChanged(rta)
}

/**
 * Does this site have an open RTA (API DEV-31)? `openRtaId` is sent to Tower Co callers only, so a
 * missing, null or 0 key means "no open RTA", never "unknown, assume yes".
 */
export function hasOpenRta (site) {
  return !!site && present(site.openRtaId) && num(site.openRtaId) > 0
}

/* ------------------------------------------------------------------------
   Step 4, the Tower Co decision (plan 2.8; decisions 41, 45 as changed, 46, 49, 55, 142)
------------------------------------------------------------------------ */

/** Is the RTA in Finalize (Step 5): Approved, submitted, locked, still open (plan 2.1)? */
export function isFinalize (rta) {
  return !!rta && rta.status === 'Finalize'
}

// The STORED decision: null (Pending) or 'Approved'. The form on screen can be ahead of it while an auto-save waits;
// the commands below judge what the server holds, after the caller has flushed.
function storedStatus (rta) {
  return tcDecision(rta).approvedStatus
}

/** Is there a decision (Approved) stored? Pending is not one; the Step 4 decision is Approve only (142). */
export function isDecided (rta) {
  return storedStatus(rta) === 'Approved'
}

/** Does the stored decision have Decision Remarks with text (an emptied editor's `<p><br></p>` is none)? */
export function hasDecisionRemarks (rta) {
  return hasRemarksText(tcDecision(rta).remarks)
}

/** May this caller change the decision (status, date, remarks)? The TC closer, the RTA in Client Approved, the decision not locked. */
export function canDecide (rta, org, profile) {
  return !!rta && rta.status === 'Client Approved' && !isTerminal(rta) && !isDecisionLocked(rta) && isTcCloser(rta, org, profile)
}

/** Unlock: the TC closer, an RTA in Finalize whose decision is locked. A terminal RTA is frozen. There is no locked rejection any more. */
export function canUnlockDecision (rta, org, profile) {
  return !!rta && rta.status === 'Finalize' && isDecisionLocked(rta) && isTcCloser(rta, org, profile)
}

/**
 * Finalize (the Submit decision button, E17): the TC closer may decide (canDecide), a stored Approved decision, and no unsubmitted
 * change to the APPROVED option's review (`approvedChangedByParty`; decision 47, I-11, P-S1: the side that changed it has to submit first).
 */
export function canFinalize (rta, org, profile) {
  return canDecide(rta, org, profile) && storedStatus(rta) === 'Approved' && !approvedChanged(rta)
}

/** Is Send E-mails on for this RTA? Only an explicit `false` is off (the API's own test, RequestMixins.requestMailOff; `sendMail` is also a flat list key, API-14). */
export function mailOn (rta) {
  return !(rta && ((rta.emails && rta.emails.sendMail === false) || rta.sendMail === false))
}

/** Is the stored decision already e-mailed, unchanged (`tc.decisionMail` set)? It picks Send or Re-send; it never disables the button. */
export function decisionEmailed (rta) {
  return !!tcDecision(rta).decisionMail
}

/**
 * The Step 4 e-mail button (decision 55, exactly RTS step 2's Send / Re-send): { visible, enabled, reason }.
 *   visible  the TC closer, an open RTA, an Approved decision and a Telco client. Pending and a Tower Co client draw nothing.
 *   enabled  Send E-mails is on.
 *   reason   when disabled: 'mail_off' (the tooltip).
 */
export function emailButton (rta, org, profile) {
  var out = { visible: false, enabled: false, reason: '' }

  if (!rta || isTerminal(rta) || !isTcCloser(rta, org, profile) || !isDecided(rta) || clientIsTowerCo(rta, org)) {
    return out
  }

  out.visible = true

  if (!mailOn(rta)) {
    out.reason = 'mail_off'
  } else {
    out.enabled = true
  }

  return out
}

/**
 * Decision 49: should the Finalize confirm warn "{Telco} hasn't been e-mailed this decision"? Only when the e-mail COULD be
 * sent and was not: an Approved decision stored, an open RTA, a Telco client, Send E-mails on, and `tc.decisionMail` null (never
 * sent, or changed since it was sent). A Tower Co client or mail off never warns, because there is nothing to send.
 */
export function decisionUnsent (rta, org) {
  return !!rta && !isTerminal(rta) && isDecided(rta) && !clientIsTowerCo(rta, org) && mailOn(rta) && !decisionEmailed(rta)
}

/**
 * The whole gate of the Step 4 bottom button, Submit decision (decision 45 as changed): { visible, mode, enabled, reason }.
 *   visible  a Tower Co user (view-only too) and the RTA in Client Approved. Finalize and every other stage draw no Step 4 button.
 *   mode     'finalize' whenever visible (there is no Close RTA on Step 4, 142).
 *   enabled  the TC closer, a stored Approved decision and no unsubmitted change (canFinalize).
 *   reason   when disabled, in this order: 'not_closer', 'pending', 'response_changed';
 *            'locked' for a decision that is somehow locked while the stage is Client Approved (no copy: it is a backstop).
 */
export function step3Action (rta, org, profile) {
  var out = { visible: false, mode: null, enabled: false, reason: '' }

  if (!rta || rta.status !== 'Client Approved' || !isTowerCoProfile(profile)) {
    return out
  }

  out.visible = true
  out.mode = 'finalize'

  // not_closer FIRST (UX spec 2.5 item 3): an editor or a view-only user is told only a manager can submit, not to set a decision it cannot set.
  if (!isTcCloser(rta, org, profile)) {
    out.reason = 'not_closer'
  } else if (storedStatus(rta) !== 'Approved') {
    out.reason = 'pending'
  } else if (approvedChanged(rta)) {
    out.reason = 'response_changed'
  } else if (canFinalize(rta, org, profile)) {
    out.enabled = true
  } else {
    out.reason = 'locked'
  }

  return out
}

/* ------------------------------------------------------------------------
   Step 5, Finalize (plan 2.11; decisions 40, 45, 46)
------------------------------------------------------------------------ */

/**
 * May this caller fill in Finalize? The TC closer (project / finance / admin manager), and only in Finalize: the decision is
 * Approved and submitted, and the RTA is open. Everyone else (a TC editor who is not a closer, a view-only user) sees it
 * read-only; the client sees the Final option block and the status panel only, with no waiting alert (decision 166). In any other stage the API answers 409 not_transitioned.
 */
export function canEditCompletion (rta, org, profile) {
  return !!rta && isFinalize(rta) && isTcCloser(rta, org, profile)
}

/** Complete RTA (creates the site, and the RTS for a Telco client): the same gate as Finalize itself. */
export function canComplete (rta, org, profile) {
  return canEditCompletion(rta, org, profile)
}
